import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedNumber } from './AnimatedNumber';
import { stackApp } from '../../stackApp';
import { publicAssetUrl } from '../../lib/publicAssetUrl';

const SCREENS = [
  { id: 'facility',   label: 'Facility',   icon: '🏭' },
  { id: 'room',       label: 'Room',       icon: '🌿' },
  { id: 'workforce',  label: 'Workforce',  icon: '👥' },
  { id: 'market',     label: 'Market',     icon: '💵' },
  { id: 'lab',         label: 'Lab',        icon: '🔬' },
  { id: 'research',   label: 'Research',   icon: '🧪' },
  { id: 'genetics',    label: 'Genetics',    icon: '🧬' },
  { id: 'leaderboard', label: 'Leaderboard', icon: '📰' },
];

const STAGE_NAMES = { 1: 'Greenhouse', 2: 'Indoor', 3: 'Industrial', 4: 'Sandbox' };

const CLOUD_STATUS_LABEL = { saving: '☁ Saving…', saved: '☁ Saved', error: '⚠ Save failed' };
const CLOUD_STATUS_COLOR = { saving: 'var(--text-muted)', saved: 'var(--green)', error: 'var(--red)' };

export function Header({ state, dispatch, user, onOpenAuth, cloudStatus, doSave, skipUnloadSave, musicMuted, onToggleMute }) {
  const unread = state.notifications.filter(n => !n.read).length;
  const cashColor = state.cash < 0 ? 'danger' : state.cash < 500 ? 'warning' : 'positive';
  const compColor = state.complianceScore >= 70 ? 'positive' : state.complianceScore >= 40 ? 'warning' : 'danger';

  const handleSignOut = async () => {
    // Push save via the shared doSave helper so status indicator updates
    if (doSave && user) {
      try {
        const token = await user.getAccessToken();
        if (token) {
          console.log('[cloud] Sign-out: pushing save (day', state.gameDay, ')...');
          await doSave(token, state);
          // Push leaderboard fire-and-forget
          const reviews = (state.publishedReviews || []).slice().sort((a, b) => b.score - a.score);
          const best = reviews[0];
          fetch('/api/leaderboard', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              display_name: user?.displayName || user?.primaryEmail || 'Anonymous Grower',
              total_revenue: state.totalRevenue || 0,
              best_quality: best?.score ?? 0,
              best_strain: best?.strainName ?? best?.channel ?? null,
              game_day: state.gameDay || 1,
              stage: state.stage || 1,
              reputation: Math.round(state.reputation || 0),
              best_review_quote: best?.quote ?? null,
            }),
          }).catch(() => {});
        } else {
          console.warn('[cloud] Sign-out: no access token, save skipped');
        }
      } catch (e) {
        console.error('[cloud] Sign-out save error:', e);
      }
    }
    localStorage.removeItem('canntycoon_save_v1');
    try { await stackApp.signOut(); } catch (e) { /* best effort */ }
    dispatch({ type: 'RESET_GAME' });
  };

  const handleSpeed = (speed) => {
    dispatch({ type: 'SET_GAME_SPEED', payload: { speed } });
    if (state.paused) dispatch({ type: 'TOGGLE_PAUSE' });
  };

  return (
    <>
      <header className="hud">
        <img
          src={publicAssetUrl('assets/logo.png')}
          alt="CannTycoon"
          style={{ height: 44, width: 'auto', objectFit: 'contain', display: 'block' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />

        <div className="hud-stats">
          <div className="hud-stat">
            <span className="stat-label">💰</span>
            <span className={`stat-value ${cashColor}`}>
              <AnimatedNumber value={state.cash} format="currency" />
            </span>
          </div>
          <div className="hud-stat">
            <span className="stat-label">📅 Day</span>
            <span className="stat-value">
              <AnimatedNumber value={state.gameDay} />
            </span>
          </div>
          <div className="hud-stat">
            <span className="stat-label">⭐ Rep</span>
            <span className="stat-value">
              <AnimatedNumber value={Math.round(state.reputation)} />
            </span>
          </div>
          <div className="hud-stat">
            <span className="stat-label">🏢</span>
            <span className="stat-value">{STAGE_NAMES[state.stage] || 'Stage ' + state.stage}</span>
          </div>
          <div className="hud-stat">
            <span className="stat-label">📋</span>
            <span className={`stat-value ${compColor}`}>{Math.round(state.complianceScore)}%</span>
          </div>
          {state.weeklyBurn > 0 && (
            <div className="hud-stat">
              <span className="stat-label">🔥 Burn</span>
              <span className="stat-value warning">${state.weeklyBurn.toLocaleString()}/wk</span>
            </div>
          )}
        </div>

        <div className="hud-controls">
          {/* Speed controls */}
          <div className="speed-toggle">
            <button className={`speed-btn ${state.gameSpeed === 1000 && !state.paused ? 'active' : ''}`} onClick={() => handleSpeed(1000)} title="1× speed (key: 1)">1×</button>
            <button className={`speed-btn ${state.gameSpeed === 400 && !state.paused ? 'active' : ''}`} onClick={() => handleSpeed(400)} title="2× speed (key: 2)">2×</button>
            <button className={`speed-btn ${state.gameSpeed === 150 && !state.paused ? 'active' : ''}`} onClick={() => handleSpeed(150)} title="4× speed (key: 3)">4×</button>
          </div>

          <button
            className={`pause-btn ${state.paused ? 'paused' : ''}`}
            onClick={() => dispatch({ type: 'TOGGLE_PAUSE' })}
          >
            {state.paused ? '▶ Play' : '⏸ Pause'}
          </button>

          {/* Notification bell */}
          <button
            className={`alert-badge ${unread > 0 ? 'has-alerts' : ''}`}
            onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: state.ui.activeScreen } })}
            title="Notifications"
          >
            🔔
            {unread > 0 && <span className="badge-count">{unread > 9 ? '9+' : unread}</span>}
          </button>

          {/* Music mute toggle */}
          {onToggleMute && (
            <button
              className="btn btn-secondary btn-sm"
              style={{ fontSize: 14, padding: '4px 8px' }}
              onClick={onToggleMute}
              title={musicMuted ? 'Unmute music' : 'Mute music'}
            >
              {musicMuted ? '🔇' : '🔊'}
            </button>
          )}

          {/* Restart */}
          <button
            className="btn btn-secondary btn-sm"
            style={{ fontSize: 11, color: 'var(--text-dim)' }}
            title="Start a new game (clears current save)"
            onClick={async () => {
              if (window.confirm('Start a new game? Your current save and leaderboard entry will be lost.')) {
                if (user) {
                  try {
                    const token = await user.getAccessToken();
                    if (token) {
                      await Promise.all([
                        fetch('/api/saves', { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
                        fetch('/api/leaderboard', { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
                      ]);
                    }
                  } catch (e) { /* best effort */ }
                }
                localStorage.removeItem('canntycoon_save_v1');
                if (skipUnloadSave) skipUnloadSave(); // prevent beforeunload from re-saving
                window.location.reload();
              }
            }}
          >⟳ New Game</button>

          {/* Auth + cloud status */}
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              {cloudStatus && (
                <span style={{ fontSize: 11, color: CLOUD_STATUS_COLOR[cloudStatus] }}>
                  {CLOUD_STATUS_LABEL[cloudStatus]}
                </span>
              )}
              {!cloudStatus && (
                <span style={{ color: 'var(--text-muted)' }}>
                  {user.displayName || user.primaryEmail || 'Synced'}
                </span>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleSignOut}
                style={{ fontSize: 11 }}
              >Sign Out</button>
            </div>
          ) : (
            <button
              className="btn btn-secondary btn-sm"
              onClick={onOpenAuth}
              title="Sign in to sync saves to cloud"
            >☁ Sign In</button>
          )}
        </div>
      </header>

      {/* Navigation */}
      <nav className="nav-tabs">
        {SCREENS.map(s => (
          <motion.button
            key={s.id}
            className={`nav-tab ${state.ui.activeScreen === s.id ? 'active' : ''}`}
            onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: s.id } })}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
          >
            {s.icon} {s.label}
          </motion.button>
        ))}
      </nav>
    </>
  );
}
